import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Filter,
  LogOut,
  MapPin,
  MoreVertical,
  Search,
  Star,
  TrendingUp,
  Users,
  X,
  XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Applications = () => {
  const { isAuthenticated, user, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: ''
  });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    applied: 0,
    underReview: 0,
    interviewed: 0,
    selected: 0,
    rejected: 0
  });

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !token) {
      navigate('/login');
      return;
    }

    fetchApplications();
  }, [authLoading, isAuthenticated, token, navigate]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const headers = {
        Authorization: `Bearer ${token}`
      };

      console.log('📤 USER APPLICATIONS - Fetching applications with token:', !!token);

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/applications`, { headers });

      console.log('📥 USER APPLICATIONS - Applications response:', response.data);

      const applicationsData = response.data?.data?.applications || [];
      const statsData = response.data?.data?.stats || {};

      setApplications(applicationsData);
      setFilteredApplications(applicationsData);
      setStats(statsData);

    } catch (error) {
      console.error('❌ USER APPLICATIONS - Error fetching applications:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setApplications([]);
      setFilteredApplications([]);
      setStats({
        total: 0,
        applied: 0,
        underReview: 0,
        interviewed: 0,
        selected: 0,
        rejected: 0
      });
      
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = applications;

    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter(app => {
        const title = app.job?.title?.toLowerCase() || '';
        const company = app.job?.company?.name?.toLowerCase() || '';
        const appId = app.applicationId?.toLowerCase() || '';
        return title.includes(term) || company.includes(term) || appId.includes(term);
      });
    }

    if (filters.status) {
      filtered = filtered.filter(app => app.status === filters.status);
    }

    if (filters.dateRange) {
      const today = new Date();
      let startDate;
      
      switch (filters.dateRange) {
        case 'week':
          startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }
      
      if (startDate) {
        filtered = filtered.filter(app => new Date(app.appliedDate) >= startDate);
      }
    }

    setFilteredApplications(filtered);
  }, [filters, applications]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'under review':
      case 'reviewing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'interview':
      case 'interviewing':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'shortlisted':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'selected':
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'applied':
        return <Clock className="w-4 h-4" />;
      case 'under review':
      case 'reviewing':
        return <Eye className="w-4 h-4" />;
      case 'interview':
      case 'interviewing':
        return <Users className="w-4 h-4" />;
      case 'shortlisted':
        return <Star className="w-4 h-4" />;
      case 'selected':
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleLogout = () => {
    logout();
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      }
    } catch (error) {
      return 'Recently';
    }
  };

  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    
    if (typeof location === 'string') {
      return location;
    }
    
    if (typeof location === 'object') {
      const { city, state, country, isRemote } = location;
      
      if (isRemote) {
        return 'Remote';
      }
      
      const parts = [];
      if (city) parts.push(city);
      if (state) parts.push(state);
      if (country && country !== 'United States') parts.push(country);
      
      return parts.length > 0 ? parts.join(', ') : 'Location not specified';
    }
    
    return 'Location not specified';
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dateRange: ''
    });
  };

  const ApplicationCard = ({ application, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="card-luxury p-6 group hover:scale-[1.02] transition-all duration-300"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4 flex-1">
          <img
            src={application.job?.company?.logo || 'https://via.placeholder.com/60x60?text=Logo'}
            alt={application.job?.company?.name || 'Company'}
            className="w-16 h-16 rounded-2xl object-cover shadow-soft group-hover:shadow-lg transition-shadow"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-heading font-bold text-slate-900 mb-2 group-hover:gradient-text-primary transition-all">
              {application.job?.title || 'Position Title'}
            </h3>
            <div className="flex items-center space-x-2 mb-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 font-medium">{application.job?.company?.name || 'Company Name'}</span>
              {application.job?.company?.verified && (
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-2 h-2 text-white" />
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4 text-sm text-slate-500">
              <div className="flex items-center space-x-1">
                <MapPin className="w-3 h-3" />
                <span>{typeof application.job?.location === 'object' ? 
                  formatLocation(application.job.location) : 
                  (application.job?.location || 'Location')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>Applied {getTimeAgo(application.appliedDate)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-3">
          <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-xl border font-medium text-sm ${getStatusColor(application.status)}`}>
            {getStatusIcon(application.status)}
            <span>{application.status || 'Pending'}</span>
          </div>
          <button
            onClick={() => {
              setSelectedApplication(application);
              setShowDetails(true);
            }}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Application ID: <span className="font-medium text-slate-900">{application.applicationId || 'N/A'}</span>
        </div>
        <Link
          to={`/job/${application.job?.id || application.job?._id}`}
          className="btn-outline text-sm px-4 py-2"
        >
          View Job
          <ArrowRight className="w-3 h-3 ml-1" />
        </Link>
      </div>
    </motion.div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
                </div>
              </Link>
              <nav className="hidden lg:flex space-x-8 ml-8">
                <Link to="/dashboard" className="nav-link">Dashboard</Link>
                <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                <Link to="/applications" className="nav-link active">Applications</Link>
                <Link to="/profile" className="nav-link">Profile</Link>
                <Link to="/about" className="nav-link">About</Link>
                <Link to="/contact" className="nav-link">Contact</Link>
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/profile" className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 transition-colors">
                <img
                  src={user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover shadow-soft"
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-heading font-semibold text-slate-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-slate-500">Job Seeker</p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-luxury py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Your Job
              <span className="block gradient-text">Applications</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Track the progress of your applications and stay updated on your career journey.
            </p>
          </div>

          <div className="flex items-center justify-center space-x-8 text-sm text-slate-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span>{filteredApplications.length} applications</span>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              <span>Success rate tracking</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-amber-400" />
              <span>Premium insights</span>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8"
        >
          <div className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold font-heading mb-1 gradient-text-primary">{stats.total || applications.length}</div>
            <div className="text-sm text-slate-600 font-medium">Total</div>
          </div>

          <div className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center shadow-lg">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold font-heading mb-1 gradient-text-primary">{stats.applied || 0}</div>
            <div className="text-sm text-slate-600 font-medium">Applied</div>
          </div>

          <div className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center shadow-lg">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold font-heading mb-1 gradient-text-primary">{stats.underReview || 0}</div>
            <div className="text-sm text-slate-600 font-medium">Under Review</div>
          </div>

          <div className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold font-heading mb-1 gradient-text-primary">{stats.interviewed || 0}</div>
            <div className="text-sm text-slate-600 font-medium">Interview</div>
          </div>

          <div className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold font-heading mb-1 gradient-text-primary">{stats.selected || 0}</div>
            <div className="text-sm text-slate-600 font-medium">Selected</div>
          </div>

          <div className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg">
              <XCircle className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold font-heading mb-1 gradient-text-primary">{stats.rejected || 0}</div>
            <div className="text-sm text-slate-600 font-medium">Rejected</div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card-luxury p-6 mb-8"
        >
          <div className="grid md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search applications..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="input-luxury pl-12"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="input-luxury"
            >
              <option value="">All Status</option>
              <option value="applied">Applied</option>
              <option value="under review">Under Review</option>
              <option value="interview">Interview</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="selected">Selected</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="input-luxury"
            >
              <option value="">All Time</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="quarter">Past 3 Months</option>
            </select>
            
            <button
              onClick={clearFilters}
              className="btn-outline"
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear Filters
            </button>
          </div>
        </motion.div>

        {/* Applications List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Loading your applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Briefcase className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">No applications found</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              {applications.length === 0 
                ? "You haven't applied to any jobs yet. Start exploring opportunities!"
                : "No applications match your current filters. Try adjusting your search criteria."
              }
            </p>
            {applications.length === 0 ? (
              <Link to="/jobs" className="btn-primary">
                Browse Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            ) : (
              <button
                onClick={clearFilters}
                className="btn-primary"
              >
                Clear Filters
                <X className="w-4 h-4 ml-2" />
              </button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-6">
            {filteredApplications.map((application, index) => (
              <ApplicationCard key={application.id || index} application={application} index={index} />
            ))}
          </div>
        )}
      </div>

      {/* Application Details Modal */}
      <AnimatePresence>
        {showDetails && selectedApplication && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="card-luxury p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">
                    Application Details
                  </h2>
                  <div className={`inline-flex items-center space-x-2 px-3 py-2 rounded-xl border font-medium ${getStatusColor(selectedApplication.status)}`}>
                    {getStatusIcon(selectedApplication.status)}
                    <span>{selectedApplication.status || 'Pending'}</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-3">Job Information</h3>
                  <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
                    <p><span className="font-medium">Position:</span> {selectedApplication.job?.title}</p>
                    <p><span className="font-medium">Company:</span> {selectedApplication.job?.company?.name}</p>
                    <p><span className="font-medium">Location:</span> {typeof selectedApplication.job?.location === 'object' ? 
                      formatLocation(selectedApplication.job.location) : 
                      (selectedApplication.job?.location || 'Not specified')}</p>
                    <p><span className="font-medium">Applied:</span> {formatDate(selectedApplication.appliedDate)}</p>
                    <p><span className="font-medium">Application ID:</span> {selectedApplication.applicationId}</p>
                  </div>
                </div>

                {selectedApplication.coverLetter && (
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 mb-3">Cover Letter</h3>
                    <div className="bg-slate-50 rounded-2xl p-4">
                      <p className="text-slate-700 leading-relaxed">{selectedApplication.coverLetter}</p>
                    </div>
                  </div>
                )}

                {selectedApplication.resume && (
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 mb-3">Resume</h3>
                    <div className="bg-slate-50 rounded-2xl p-4">
                      <a
                        href={selectedApplication.resume.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>View Resume</span>
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="btn-outline"
                  >
                    Close
                  </button>
                  <Link
                    to={`/job/${selectedApplication.job?.id || selectedApplication.job?._id}`}
                    className="btn-primary"
                  >
                    View Job Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Applications; 